import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  LinkOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";
import "../../../styles/Main.scss";
import SearchIcon from "../../../image/SearchIcon.svg";
import InvoiceLinkChallansModal from "./InvoiceLinkChallansModal";

// Inspection statuses: orange/amber palette
const inspectionStatusStyles = {
  "Pending for inspection": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
  "Inspection HOLD item": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
  "Partially Inspected": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
  "Pending QC": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
  "PENDING QC": {
    color: "#d46b08",
    background: "#fff7e6",
    borderColor: "#ffd591",
  },
  "Inspection completed": {
    color: "#389e0d",
    background: "#f6ffed",
    borderColor: "#b7eb8f",
  },
  Verified: {
    color: "#389e0d",
    background: "#f6ffed",
    borderColor: "#b7eb8f",
  },
};

// Distribution statuses: blue/purple/teal palette (distinct from inspection)
const distributionStatusStyles = {
  "Pending for distribution": {
    color: "#0958d9",
    background: "#e6f4ff",
    borderColor: "#91caff",
  },
  "Distribute HOLD item": {
    color: "#722ed1",
    background: "#f9f0ff",
    borderColor: "#d3adf7",
  },
  "Distribution completed": {
    color: "#08979c",
    background: "#e6fffb",
    borderColor: "#87e8de",
  },
};

const statusOptionStyles = {
  ...inspectionStatusStyles,
  ...distributionStatusStyles,
};

const StatusOptionTag = ({ value, label }) => {
  const style = statusOptionStyles[value] || {
    color: "#595959",
    background: "#fafafa",
    borderColor: "#d9d9d9",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 12px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 500,
        color: style.color,
        background: style.background,
        border: `1px solid ${style.borderColor}`,
      }}
    >
      {label}
    </span>
  );
};

StatusOptionTag.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
};

/** ISO datetime like 2026-03-31T01:08:28.040452 → localized short date/time */
function formatLinkedAtDisplay(raw) {
  if (raw == null || raw === "") return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString("en-US", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(raw);
  }
}

const linkedInvoiceExpandTableColumns = (setDocumentPreviewUrl) => [
  {
    title: "Invoice number",
    dataIndex: "invoice_number",
    key: "invoice_number",
    width: 140,
    ellipsis: true,
    render: (v) => v ?? "—",
  },
  {
    title: "Invoice date",
    dataIndex: "invoice_date",
    key: "invoice_date",
    width: 120,
    render: (v) => v ?? "—",
  },
  {
    title: "Vendor",
    dataIndex: "vendor",
    key: "vendor",
    width: 200,
    ellipsis: true,
    render: (v) => v ?? "—",
  },
  // {
  //   title: "Invoice ID",
  //   dataIndex: "invoice_id",
  //   key: "invoice_id",
  //   width: 260,
  //   ellipsis: true,
  //   render: (v) => (v != null ? String(v) : "—"),
  // },
  {
    title: "Linked at",
    dataIndex: "created_at",
    key: "created_at",
    width: 200,
    render: (v) => formatLinkedAtDisplay(v),
  },
  {
    title: "Document",
    key: "invoice_file_url",
    width: 110,
    align: "center",
    render: (_, row) =>
      row.invoice_file_url ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setDocumentPreviewUrl(row.invoice_file_url)}
        >
          View file
        </Button>
      ) : (
        "—"
      ),
  },
];

const linkedChallanExpandTableColumns = (setDocumentPreviewUrl) => [
  {
    title: "Challan Ref.",
    dataIndex: "challan_number",
    key: "challan_number",
    width: 140,
    ellipsis: true,
    render: (v) => v ?? "—",
  },
  {
    title: "Challan date",
    dataIndex: "challan_date",
    key: "challan_date",
    width: 120,
    render: (v) => v ?? "—",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 200,
    ellipsis: true,
    render: (v) => v ?? "—",
  },
  {
    title: "Supplier",
    dataIndex: "supplier_name",
    key: "supplier_name",
    width: 200,
    ellipsis: true,
    render: (v) => v ?? "—",
  },
  {
    title: "Line items",
    key: "item_count",
    width: 90,
    align: "center",
    render: (_, row) =>
      Array.isArray(row.challan_items) ? row.challan_items.length : 0,
  },
  {
    title: "Delivery address",
    dataIndex: "delivery_address",
    key: "delivery_address",
    width: 200,
    render: (text) => {
      const full = text ?? "";
      if (!full) return "—";
      return (
        <Tooltip title={full}>
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: "18px",
              maxHeight: 36,
            }}
          >
            {full}
          </span>
        </Tooltip>
      );
    },
  },
  {
    title: "Document",
    key: "document_file_url",
    width: 110,
    align: "center",
    render: (_, row) =>
      row.document_file_url ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setDocumentPreviewUrl(row.document_file_url)}
        >
          View file
        </Button>
      ) : (
        "—"
      ),
  },
];

function formatChallanGrnDateTime(row) {
  const raw =
    row?.created_at ??
    row?.updated_at ??
    row?.challan_date ??
    row?.grn_created_date ??
    null;
  if (raw == null || raw === "") return "N/A";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString("en-US", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(raw);
  }
}

const GoodsRecieptGateList = ({
  embedded,
  refreshKey,
  onInspect,
  documentType,
} = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const today = new Date();
  const { Search } = Input;
  const CompanyId = authUserData()?.company || null;

  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [businessSettings, setBusinessSettings] = useState({
    material_received_inspection: false,
    inspection_before_distribution: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [challanLinkModalOpen, setChallanLinkModalOpen] = useState(false);
  const [challanLinkRecord, setChallanLinkRecord] = useState(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [siteLocationList, setSiteLocationList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
    search: "",
    site: undefined,
    vendor: undefined,
    status: "",
    start_date: "",
    end_date: "",
  });
  const [filterSelection, setFilterSelection] = useState({
    site: undefined,
    vendor: undefined,
    status: "",
    startDate: null,
    endDate: null,
  });
  const [searchInputValue, setSearchInputValue] = useState("");

  const fetchGoodsReceipts = async () => {
    try {
      setLoading(true);
      const params = { page: state.page, pagesize: state.pagesize };
      if (state.search?.trim()) params.search = state.search.trim();
      if (state.site != null && state.site !== "") params.site = state.site;
      if (state.vendor != null && state.vendor !== "")
        params.vendor = state.vendor;
      if (state.status != null && state.status !== "")
        params.status = state.status;
      if (state.start_date) params.start_date = state.start_date;
      if (state.end_date) params.end_date = state.end_date;
      if (documentType) params.document_type = documentType;

      const endpoint = "inventory-goods-receipts/";
      const res = await getRequest(endpoint, params);
      const payload = res?.data;
      const count = payload?.count ?? payload?.results?.count ?? 0;
      setTotalCount(count);
      const results = payload?.results;
      const settings = results?.business_settings;
      if (settings) {
        setBusinessSettings({
          material_received_inspection: !!settings.material_received_inspection,
          inspection_before_distribution:
            settings.inspection_before_distribution ?? null,
        });
      } else if (documentType === "challan") {
        setBusinessSettings({
          material_received_inspection: true,
          inspection_before_distribution: "inspect_first",
        });
      }
      const data = results?.data ?? results ?? payload?.data ?? payload ?? [];
      const list = Array.isArray(data) ? data : [];

      if (list.length > 0) {
        const mapped = list.map((item) => {
          const row =
            documentType === "challan"
              ? {
                  ...((item && item.linked_invoice) || {}),
                  ...((item && item.invoice) || {}),
                  ...((item && item.challan) || {}),
                  ...item,
                }
              : item;
          const receivedRaw = row?.received_date ?? row?.updated_at;
          let received = "—";
          if (receivedRaw) {
            try {
              const d = new Date(receivedRaw);
              received = d.toLocaleString("en-US", {
                dateStyle: "short",
                timeStyle: "short",
              });
            } catch (e) {
              received = String(receivedRaw);
            }
          }
          const rawStatus = row?.status;
          const status =
            String(rawStatus).toUpperCase() === "PENDING QC"
              ? "Pending QC"
              : rawStatus;
          const itemCount =
            documentType === "challan"
              ? Array.isArray(row?.challan_items)
                ? row.challan_items.length
                : 0
              : Array.isArray(row?.items)
                ? row.items.length
                : 0;

          const documentRef =
            documentType === "challan"
              ? (row?.challan_number ?? "N/A")
              : (row?.invoice_number ?? "N/A");

          const documentIdForFlow =
            row?.document != null && row?.document !== ""
              ? row.document
              : (row?.id ?? row?.challan_id);

          const supplierName =
            documentType === "challan"
              ? (row?.supplier_name ??
                row?.supplier ??
                row?.vendor_name ??
                row?.supplier_address ??
                "N/A")
              : row?.vendor_details?.name
                ? row?.vendor_details?.name
                : (row?.vendor_name ?? "N/A");

          const siteName =
            documentType === "challan"
              ? (row?.site_name ??
                row?.delivery_site_name ??
                row?.delivery_address ??
                "N/A")
              : (row?.site_name ?? "N/A");

          const grnCreatedForUi =
            documentType === "challan"
              ? (row?.grn_created_date ?? formatChallanGrnDateTime(row))
              : (row?.grn_created_date ?? "N/A");
          const rowKey = row?.id ?? row?.challan_id ?? documentIdForFlow;
          return {
            key: rowKey,
            id: row?.id ?? row?.challan_id,
            status,
            grnId: row?.grn_number ?? `GRN-${rowKey}`,
            document: documentRef,
            supplier: supplierName,
            invoice_number: documentRef,
            grn_created_date: grnCreatedForUi,
            inspectionBy: row?.inspection_by_name ?? row?.inspected_by ?? "N/A",
            distributionBy:
              row?.distribution_by_name ?? row?.distributed_by ?? "N/A",
            site: siteName,
            documentID: documentIdForFlow,
            document_file_url: row?.document_file_url ?? "",
            received_date: row?.received_date,
            itemCount,
            inspected_by: row?.inspected_by ?? null,
            distributed_by: row?.distributed_by ?? null,
            linked_invoice:
              documentType === "challan"
                ? (item?.linked_invoice ?? null)
                : null,
            linked_challans:
              documentType === "invoice"
                ? Array.isArray(item?.linked_challans)
                  ? item.linked_challans
                  : []
                : [],
            invoice_id: documentType === "invoice" ? row?.invoice_id ?? null : null,
            invoice_date_raw:
              documentType === "invoice"
                ? (row?.invoice_date ??
                  row?.date ??
                  row?.document_date ??
                  row?.bill_date ??
                  null)
                : null,
          };
        });
        setTableData(mapped);
      } else {
        setTableData([]);
      }
    } catch (err) {
      ShowErrorMsg(err);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setState((prev) => ({
      ...prev,
      page: pagination.current,
      pagesize: pagination.pageSize,
    }));
  };

  const onSearch = (value) => {
    setState((prev) => ({ ...prev, search: value ?? "", page: 1 }));
  };
  const debouncedSearchRef = useRef(
    debounce((value) => onSearch(value), 800),
  ).current;
  const searchCallbackDelayed = (e) => {
    const value = e?.target?.value ?? "";
    setSearchInputValue(value);
    debouncedSearchRef(value);
  };

  const user = authUserData().first_name ? authUserData().first_name : null;
  const userName = user || "N/A";

  const getCompanySites = async () => {
    if (!CompanyId) return;
    try {
      const res = await getRequest(
        `delivery-address/${CompanyId}/get-address-by-company/`,
      );
      const data = res?.data?.results?.data ?? res?.data?.data ?? [];
      setSiteLocationList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGoodsReceipts();
  }, [state, documentType]);

  useEffect(() => {
    if (CompanyId) getCompanySites();
  }, [CompanyId]);

  const prevRefreshKeyRef = useRef(undefined);
  useEffect(() => {
    if (!embedded || refreshKey == null) return;
    const prev = prevRefreshKeyRef.current;
    prevRefreshKeyRef.current = refreshKey;
    if (prev === undefined) return;
    fetchGoodsReceipts();
  }, [embedded, refreshKey]);

  const columns = [
    {
      title: "Site Name",
      dataIndex: "site",
      key: "site",
      width: 180,
      render: (value) => {
        const text = value ?? "—";
        return (
          <Tooltip title={text}>
            <span
              style={{
                display: "inline-block",
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "bottom",
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      width: 220,
      render: (value) => {
        const text = value ?? "—";
        return (
          <Tooltip title={text}>
            <span
              style={{
                display: "inline-block",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "bottom",
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      width: 180,
      render: (_, record) => {
        const status = record.status;
        const displayLabel =
          status === "Verified" ? "Confirm Inspected" : (status ?? "—");
        return <StatusOptionTag value={status} label={displayLabel} />;
      },
    },
    {
      title: documentType === "challan" ? "Challan Ref." : "Invoice Ref.",
      dataIndex: "invoice_number",
      key: "invoice_number",
    },
    ...(documentType === "challan"
      ? [
          {
            title: "Linked invoice",
            key: "linked_invoice",
            width: 140,
            render: (_, record) => {
              const li = record.linked_invoice;
              if (!li?.invoice_id && !li?.invoice_date) return "—";
              const label = li.invoice_date ?? "Linked";
              return (
                <Tooltip
                  title={
                    li.invoice_id
                      ? `${li.vendor ?? ""}${li.vendor ? " · " : ""}${String(li.invoice_id)}`
                      : ""
                  }
                >
                  <span>{label}</span>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    {
      title: "GRN ID",
      dataIndex: "grnId",
      key: "grnId",
      render: (value) => (
        <span style={{ fontWeight: "bold", color: "#094e8f" }}>{value}</span>
      ),
    },
    {
      title: "GRN Date & Time",
      dataIndex: "grn_created_date",
      key: "grn_created_date",
    },
    {
      title: "Inspection By",
      dataIndex: "inspectionBy",
      key: "inspectionBy",
    },
    {
      title: "Distribution By",
      dataIndex: "distributionBy",
      key: "distributionBy",
    },
    {
      title: "Action",
      width: 176,
      align: "center",
      render: (_, record) => {
        const goToAcceptance = (mode, docId) => {
          if (embedded && onInspect) onInspect(mode, docId);
          else {
            const search =
              documentType != null && documentType !== ""
                ? `?document_type=${encodeURIComponent(String(documentType))}`
                : "";
            navigate(
              `/inventory-acceptance-and-distribution/${mode}/${docId}${search}`,
            );
          }
        };
        const viewDocumentIcon = record.document_file_url ? (
          <Tooltip key="doc" title="View document">
            <FileTextOutlined
              style={{ fontSize: 18, cursor: "pointer", color: "#094e8f" }}
              onClick={() => setDocumentPreviewUrl(record.document_file_url)}
            />
          </Tooltip>
        ) : null;

        const materialInspection =
          businessSettings.material_received_inspection;
        const inspectionOrder = businessSettings.inspection_before_distribution;
        const inspectedBy = record.inspected_by;
        const distributedBy = record.distributed_by;
        const hasInspected = inspectedBy != null && inspectedBy !== "";
        const hasDistributed = distributedBy != null && distributedBy !== "";

        let showInspect = false;
        let showDistribute = false;
        const isPartiallyInspected =
          record.status === "Partially Inspected" ||
          record.status.includes("HOLD");

        if (!materialInspection) {
          showDistribute = true;
        } else {
          if (inspectionOrder === "inspect_first") {
            if (!hasInspected) {
              showInspect = true;
            } else {
              showDistribute = true;
            }
          } else if (inspectionOrder === "distribute_first") {
            if (!hasDistributed) {
              showDistribute = true;
            }
          }
        }

        if (hasInspected && hasDistributed) {
          showInspect = false;
          showDistribute = false;
        }

        const invoiceHasLinkedChallans =
          documentType === "invoice" &&
          Array.isArray(record.linked_challans) &&
          record.linked_challans.length > 0;
        if (invoiceHasLinkedChallans) {
          showInspect = false;
          showDistribute = false;
        }

        const inspectLabel = isPartiallyInspected
          ? "Resume Inspection"
          : "Inspect";
        const distributeLabel =
          record.status === "Inspection completed" &&
          showDistribute &&
          hasInspected &&
          !hasDistributed
            ? "Distribute"
            : showDistribute && hasInspected && !hasDistributed
              ? "Resume Distribution"
              : "Distribute";
        const manageLinkedChallansIcon = invoiceHasLinkedChallans ? (
            <Tooltip key="unlink-challans" title="Manage linked challans" placement="left">
              <LinkOutlined
                style={{ fontSize: 18, cursor: "pointer", color: "#094e8f" }}
                onClick={() => {
                  setChallanLinkRecord(record);
                  setChallanLinkModalOpen(true);
                }}
              />
            </Tooltip>
          ) : null;

        const actions = [
          viewDocumentIcon,
          showInspect && (
            <Tooltip key="inspect" title={inspectLabel}>
              <SearchOutlined
                style={{ fontSize: 18, cursor: "pointer", color: "#094e8f" }}
                onClick={() => goToAcceptance("inspection", record.documentID)}
              />
            </Tooltip>
          ),
          showDistribute && (
            <Tooltip key="distribute" title={distributeLabel}>
              <SendOutlined
                style={{ fontSize: 18, cursor: "pointer", color: "#094e8f" }}
                onClick={() =>
                  goToAcceptance("distribution", record.documentID)
                }
              />
            </Tooltip>
          ),
          !showInspect &&
            !showDistribute &&
            !invoiceHasLinkedChallans && (
            <Tooltip key="view" title="View">
              <EyeOutlined
                style={{ fontSize: 18, cursor: "pointer", color: "#094e8f" }}
                onClick={() => goToAcceptance("view", record.documentID)}
              />
            </Tooltip>
          ),
          manageLinkedChallansIcon,

        ].filter(Boolean);

        return <Space size="middle">{actions}</Space>;
      },
    },
  ];

  const rowHasExpandableLinkContent = (record) => {
    if (documentType === "challan") {
      const li = record.linked_invoice;
      if (!li || typeof li !== "object" || Array.isArray(li)) return false;
      return !!(
        li.invoice_id ||
        li.invoice_number ||
        li.vendor ||
        li.invoice_date ||
        li.invoice_file_url
      );
    }
    if (documentType === "invoice") {
      return (
        Array.isArray(record.linked_challans) &&
        record.linked_challans.length > 0
      );
    }
    return false;
  };

  const tableExpandable =
    documentType === "challan" || documentType === "invoice"
      ? {
          expandedRowRender: (record) => {
            const expandTableWrapStyle = {
              margin: 0,
              padding: "8px 16px 12px 48px",
              background: "#fafafa",
            };
            if (documentType === "challan") {
              const li = record.linked_invoice;
              if (!li) {
                return (
                  <div style={{ ...expandTableWrapStyle, color: "#999" }}>
                    No linked invoice.
                  </div>
                );
              }
              return (
                <div style={expandTableWrapStyle}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#094e8f",
                      marginBottom: 8,
                    }}
                  >
                    Linked invoice
                  </div>
                  <Table
                    size="small"
                    bordered
                    pagination={false}
                    showHeader
                    tableLayout="fixed"
                    scroll={{ x: "max-content" }}
                    columns={linkedInvoiceExpandTableColumns(
                      setDocumentPreviewUrl,
                    )}
                    dataSource={[
                      {
                        ...li,
                        key: "linked-invoice",
                      },
                    ]}
                  />
                </div>
              );
            }
            const challans = record.linked_challans || [];
            return (
              <div style={expandTableWrapStyle}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#094e8f",
                    marginBottom: 8,
                  }}
                >
                  Linked challans
                </div>
                <Table
                  size="small"
                  bordered
                  pagination={false}
                  showHeader
                  tableLayout="fixed"
                  scroll={{ x: "max-content" }}
                  columns={linkedChallanExpandTableColumns(
                    setDocumentPreviewUrl,
                  )}
                  dataSource={challans.map((c, idx) => ({
                    ...c,
                    key: c.id ?? c.document ?? idx,
                  }))}
                />
              </div>
            );
          },
          rowExpandable: (record) => rowHasExpandableLinkContent(record),
          expandIcon: ({ expanded, onExpand, record }) => {
            if (!rowHasExpandableLinkContent(record)) {
              return <span style={{ display: "inline-block", width: 24 }} />;
            }
            return (
              <button
                type="button"
                onClick={(e) => onExpand(record, e)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#094e8f",
                  padding: "0 6px",
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                }}
                aria-expanded={expanded}
                aria-label={expanded ? "Collapse row" : "Expand row"}
              >
                {expanded ? <MinusOutlined /> : <PlusOutlined />}
              </button>
            );
          },
        }
      : undefined;

  return (
    <>
      {!embedded && (
        <div className="ListingTopBar">
          <div className="pagenamewrap">
            <div className="pagename">
              <Tooltip
                placement="right"
                title="Accept incoming materials after verification and distribute them according to approved requests."
              >
                <h3>
                  Material Acceptance & Distribution Queue (Pending QC
                  Inspection)
                </h3>
              </Tooltip>

              <Breadcrumb className="Breadcrumb-Sec">
                <Breadcrumb.Item>
                  <Link to="/dashboard">{t("home")}</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Inventory Goods Receipt List</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
        </div>
      )}

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="po-good-header">
            <div className="po-header-details" style={{ margin: "0" }}>
              <div className="po-row">
                <strong>User :</strong>
                <span>{userName}</span>
              </div>
            </div>

            <Space wrap size={10}>
              <div className="searchwrp">
                <Space direction="vertical">
                  <Search
                    prefix={
                      <img
                        src={SearchIcon}
                        alt="SearchIcon"
                        className="SearchIcon"
                        width={22}
                      />
                    }
                    allowClear
                    placeholder="Search"
                    value={searchInputValue}
                    onChange={searchCallbackDelayed}
                  />
                </Space>
              </div>
              <Select
                placeholder="Select Site"
                allowClear
                value={filterSelection.site ?? undefined}
                onChange={(v) =>
                  setFilterSelection((prev) => ({ ...prev, site: v }))
                }
                options={[
                  ...siteLocationList.map((s) => ({
                    label: s?.site_name ?? s?.company_name ?? s?.id,
                    value: s?.id ?? s?.site_name,
                  })),
                ]}
              />
              <Select
                placeholder="Select Status"
                allowClear
                value={filterSelection.status || undefined}
                onChange={(v) =>
                  setFilterSelection((prev) => ({
                    ...prev,
                    status: v ?? "",
                  }))
                }
                style={{ width: 200 }}
                optionLabelProp="value"
                options={[
                  {
                    value: "Pending for inspection",
                    label: (
                      <StatusOptionTag
                        value="Pending for inspection"
                        label="Pending for inspection"
                      />
                    ),
                  },
                  {
                    value: "Pending for distribution",
                    label: (
                      <StatusOptionTag
                        value="Pending for distribution"
                        label="Pending for distribution"
                      />
                    ),
                  },
                  {
                    value: "Inspection HOLD item",
                    label: (
                      <StatusOptionTag
                        value="Inspection HOLD item"
                        label="Inspection HOLD item"
                      />
                    ),
                  },
                  {
                    value: "Distribute HOLD item",
                    label: (
                      <StatusOptionTag
                        value="Distribute HOLD item"
                        label="Distribute HOLD item"
                      />
                    ),
                  },
                  {
                    value: "Inspection completed",
                    label: (
                      <StatusOptionTag
                        value="Inspection completed"
                        label="Inspection completed"
                      />
                    ),
                  },
                  {
                    value: "Distribution completed",
                    label: (
                      <StatusOptionTag
                        value="Distribution completed"
                        label="Distribution completed"
                      />
                    ),
                  },
                ]}
              />

              <DatePicker
                placeholder="Start date"
                value={filterSelection.startDate}
                onChange={(val) =>
                  setFilterSelection((prev) => ({
                    ...prev,
                    startDate: val,
                  }))
                }
                style={{ width: 130 }}
              />
              <DatePicker
                placeholder="End date"
                value={filterSelection.endDate}
                onChange={(val) =>
                  setFilterSelection((prev) => ({
                    ...prev,
                    endDate: val,
                  }))
                }
                style={{ width: 130 }}
              />
              <Button
                type="primary"
                icon={
                  <Tooltip title="Apply filters">
                    <FilterOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    site: filterSelection.site,
                    vendor: filterSelection.vendor,
                    status: filterSelection.status,
                    start_date: filterSelection.startDate
                      ? filterSelection.startDate.format("YYYY-MM-DD")
                      : "",
                    end_date: filterSelection.endDate
                      ? filterSelection.endDate.format("YYYY-MM-DD")
                      : "",
                    page: 1,
                  }))
                }
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
              />
              <Button
                type="primary"
                icon={
                  <Tooltip title="Reset filters">
                    <UndoOutlined style={{ fontSize: "20px" }} />
                  </Tooltip>
                }
                onClick={() => {
                  setFilterSelection({
                    site: undefined,
                    status: "",
                    startDate: null,
                    endDate: null,
                  });
                  setSearchInputValue("");
                  setState((prev) => ({
                    ...prev,
                    search: "",
                    site: undefined,
                    status: "",
                    start_date: "",
                    end_date: "",
                    page: 1,
                  }));
                }}
                style={{
                  backgroundColor: "#094e8f",
                  color: "#ffffff",
                  border: "none",
                  width: "40px",
                  height: "40px",
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                }}
              />
            </Space>
          </div>

          <div className="tablewrp">
            <Table
              rowKey={(record) => record.id ?? record.key}
              columns={columns}
              dataSource={tableData}
              expandable={tableExpandable}
              bordered
              loading={loading}
              onChange={handleTableChange}
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                defaultCurrent: state.page,
                defaultPageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                locale: {
                  items_per_page: "Items / Page",
                },
                showTotal: (total) => `Total ${total} items`,
              }}
            />
          </div>
        </div>
      </section>

      <Modal
        open={documentPreviewUrl != null}
        title="View document"
        className="ModelPopupSec document-preview-modal"
        wrapClassName="modal-wrapper document-preview-modal-wrap"
        footer={null}
        onCancel={() => setDocumentPreviewUrl(null)}
        width="76vw"
        style={{ top: 16, maxWidth: "60vw" }}
        bodyStyle={{ padding: 0, margin: 0, width: "100%", maxWidth: "100%" }}
        destroyOnClose
      >
        {documentPreviewUrl &&
          (() => {
            const hasHash = documentPreviewUrl.includes("#");
            const joinChar = hasHash ? "&" : "#";
            const iframeSrc = `${documentPreviewUrl}${joinChar}zoom=100`;
            return (
              <div style={{ width: "100%", margin: 0, padding: 0 }}>
                <iframe
                  title="Document preview"
                  src={iframeSrc}
                  style={{
                    display: "block",
                    width: "100%",
                    minWidth: "100%",
                    height: "88vh",
                    border: "none",
                  }}
                />
              </div>
            );
          })()}
      </Modal>

      <InvoiceLinkChallansModal
        open={challanLinkModalOpen}
        onClose={() => {
          setChallanLinkModalOpen(false);
          setChallanLinkRecord(null);
        }}
        onSuccess={() => {
          fetchGoodsReceipts();
        }}
        invoiceUuid={
          challanLinkRecord?.invoice_id != null &&
          String(challanLinkRecord.invoice_id).includes("-")
            ? String(challanLinkRecord.invoice_id)
            : null
        }
        invoicePk={
          challanLinkRecord?.documentID ?? challanLinkRecord?.id ?? null
        }
        initialVendorName={challanLinkRecord?.supplier}
        invoiceNumber={challanLinkRecord?.invoice_number}
        invoiceDateRaw={challanLinkRecord?.invoice_date_raw}
        allowEmptySelection
      />

      <Modal
        open={modalOpen}
        title="Confirm Physical Count"
        className="ModelPopupSec"
        wrapClassName="modal-wrapper"
        footer={null}
        onCancel={() => setModalOpen(false)}
      >
        <p>
          Are you sure you want to start the inspection for this goods receipt?
        </p>

        <div className="good-modal-footer">
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>

          <Button
            type="primary"
            onClick={() => {
              message.success("Inspection started successfully");
              setModalOpen(false);
              navigate("/inventory-acceptance-and-distribution");
            }}
          >
            Confirm
          </Button>
        </div>
      </Modal>
    </>
  );
};

GoodsRecieptGateList.propTypes = {
  embedded: PropTypes.bool,
  refreshKey: PropTypes.number,
  onInspect: PropTypes.func,
  documentType: PropTypes.oneOf(["invoice", "challan"]),
};

export default GoodsRecieptGateList;
